<template>
  <div
    class="fixed top-0 left-0 w-screen h-screen overflow-auto bg-slate-100 scrollbar-lg"
    :style="{ zIndex: 999 }"
  >
    <div class="sticky top-0 z-30">
      <div
        class="flex flex-wrap items-center justify-between flex-shrink-0 h-10 px-6 lg:flex-no-wrap gap-y-2 gap-x-4 bg-pure-white"
      >
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div
        class="flex items-center justify-between px-6 border-b h-14 bg-pure-white border-slate-400"
      >
        <BlTextTitle4 :text="title" />
        <BlInputButton
          type="plain"
          icon="close"
          @clicked="$emit('close-modal')"
        />
      </div>
    </div>

    <div
      v-if="modalLoading"
      class="flex flex-col items-center justify-center w-full gap-4 py-8"
      :style="{ minHeight: 'calc(100vh - 2.5rem - 3.5rem - 5rem)' }"
    >
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="sbtStep === 0"
      class="flex flex-col items-center w-full gap-4 py-8"
      :style="{ minHeight: 'calc(100vh - 2.5rem - 3.5rem - 5rem)' }"
    >
      <div
        class="flex flex-col gap-4 px-10 py-10 border w-152 bg-pure-white rounded-3xl border-slate-400"
      >
        <BlTextSubtitle1 text="Target details" />
        <div class="grid grid-cols-2 gap-2">
          <BlInputText
            class="col-span-2"
            :input="nameInput"
            @changed="inputHandler"
          />
          <BlSelectBox
            :class="{ 'col-span-2': kind === 'client' }"
            label="Data collection method"
            asterisk
            v-model="typeInput"
            placeholder="Select type"
            :options="filterTypeOptions"
            :disabled="duplicateTargetId !== null"
            @change="typeInput = $event"
          />
          <BlSelectBox
            label="Curriculum"
            asterisk
            search
            v-model="curriculumIdInput"
            placeholder="Select curriculum"
            :options="defaultCurriculumOptions"
            :disabled="defaultCurriculum"
            @change="curriculumIdInput = $event"
          />
          <BlSelectBox
            v-if="kind === 'client'"
            label="Status"
            asterisk
            v-model="statusInput"
            placeholder="Select status"
            :options="statusOptions"
            @change="statusInput = $event"
          />
        </div>
        <BlInputText :input="descriptionInput" @changed="inputHandler" />
        <div
          v-if="typeInput.includes('Percentage')"
          class="grid grid-cols-2 gap-4"
        >
          <BlInputText :input="goalInput" @changed="inputHandler" />
          <BlSelectBox
            label="Success metric"
            asterisk
            v-model="successMetricInput"
            placeholder="Select success metric"
            :options="successMetricOptions"
            @change="successMetricInput = $event"
          />
          <BlInputText :input="trialsInput" @changed="inputHandler" />
        </div>
        <div
          v-if="typeInput.includes('Duration')"
          class="grid grid-cols-2 gap-4"
        >
          <BlInputTime :input="timeInput" @changed="inputHandler" />
          <BlSelectBox
            label="Success metric"
            asterisk
            v-model="successMetricInput"
            placeholder="Select success metric"
            :options="successMetricOptions"
            @change="successMetricInput = $event"
          />
        </div>
        <div v-if="typeInput.includes('Pir')" class="grid grid-cols-2 gap-4">
          <BlInputText :input="intervalInput" @changed="inputHandler" />
          <BlInputText :input="durationInput" @changed="inputHandler" />
          <BlInputText :input="goalInput" @changed="inputHandler" />
          <BlSelectBox
            label="Success metric"
            asterisk
            v-model="successMetricInput"
            placeholder="Select success metric"
            :options="successMetricOptions"
            @change="successMetricInput = $event"
          />
        </div>
        <div
          v-if="typeInput.includes('Frequency')"
          class="grid grid-cols-2 gap-4"
        >
          <BlInputText :input="goalFrequencyInput" @changed="inputHandler" />
          <BlSelectBox
            label="Success metric"
            asterisk
            v-model="successMetricInput"
            placeholder="Select success metric"
            :options="successMetricOptions"
            @change="successMetricInput = $event"
          />
        </div>
      </div>

      <div
        v-if="typeInput.includes('Percentage')"
        class="flex flex-col gap-4 px-10 py-10 border w-152 bg-pure-white rounded-3xl border-slate-400"
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <div :style="{ transform: 'scale(1.375)' }" class="px-2">
              <BlInputToggle
                :checked="probingEnableInput"
                @changed="probingEnableInput = !probingEnableInput"
              />
            </div>
            <BlTextSubtitle1 text="Probing" />
          </div>
          <BlTextBody2
            text="Probing can be employed to assess a skill at various stages of the therapy. Activating probing gives you additional option to conduct probes during the session."
            class="text-slate-700"
          />
        </div>
        <div v-if="probingEnableInput" class="grid grid-cols-2 gap-4">
          <BlInputText :input="probingTrialInput" @changed="inputHandler" />
          <BlInputText :input="probingGoalInput" @changed="inputHandler" />
        </div>
      </div>

      <div
        v-if="typeInput.includes('Prompting')"
        class="flex flex-col gap-4 px-10 py-10 border w-152 bg-pure-white rounded-3xl border-slate-400"
      >
        <div class="flex flex-col gap-2">
          <BlTextSubtitle1 text="Prompting format" />
          <div class="flex h-10 px-1 py-1 my-2 rounded-lg bg-slate-400">
            <div
              v-for="opt in promptingFormatOptions"
              :key="opt.value"
              class="flex items-center justify-center w-full transition-all duration-300 rounded cursor-pointer"
              :class="[
                promptingFormatInput === opt.value
                  ? 'bg-pure-white'
                  : 'bg-slate-400'
              ]"
              @click="promptingFormatInput = opt.value"
            >
              <BlTextSubtitle3 :text="opt.label" />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <BlTextBody2
              :text="
                promptingFormatInput === 'classic'
                  ? 'Frequency-based prompting'
                  : 'Score-based prompting'
              "
              class="font-bold"
            />
            <BlTextBody2
              v-if="promptingFormatInput === 'classic'"
              text="Tracks how many times each type of prompt is used during the session."
            />
            <BlTextBody2
              v-if="promptingFormatInput === 'custom'"
              text="Calculates a percentage score based on the prompts used and the values you assign to each one."
            />
          </div>
          <div
            class="flex w-full gap-2 px-2 py-2 text-sm rounded bg-cornflower-200"
          >
            <BlIcon
              icon="toast-info"
              color="cornflower-8"
              class="w-4 h-4 mt-1"
            />
            <div>
              <BlTextBody2
                text="Customize default prompts for the entire center by creating them in the Repository."
                class="text-cornflower-800"
              />
              <router-link
                :to="
                  `/repository/prompting${
                    promptingFormatInput === 'custom' ? '?tab=custom' : ''
                  }`
                "
                target="_blank"
              >
                <BlTextBody2
                  class="font-bold text-cornflower-800 hover:underline"
                  text="Create default prompts"
                />
              </router-link>
            </div>
          </div>
        </div>

        <div
          v-if="promptLoading"
          class="flex items-center justify-center w-full h-100"
        >
          <div class="blum-loader"></div>
        </div>
        <div v-else class="flex flex-col gap-4">
          <div
            class="flex items-center justify-center border rounded h-72 bg-slate-100 border-slate-400"
          >
            <PromptingPreview
              :prompts="defaultPrompts"
              :style="{ transform: 'scale(0.5 , 0.5)' }"
            />
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <div
                v-if="promptingFormatInput === 'classic'"
                class="flex-shrink-0 w-4"
              />
              <BlTextSubtitle4
                v-if="promptingFormatInput === 'custom'"
                text="Score (%)"
                class="flex-shrink-0 w-20"
              />
              <BlTextSubtitle4 text="Code" class="flex-shrink-0 w-10" />
              <BlTextSubtitle4 text="Symbol" class="flex-shrink-0 w-15" />
              <BlTextSubtitle4 text="Prompt name" class="w-full" />
              <div class="flex-shrink-0 w-6" />
            </div>

            <draggable
              v-if="promptingFormatInput === 'classic'"
              handle=".handle"
              @update.prevent="onMovePosition($event)"
              class="flex flex-col gap-3"
            >
              <div
                v-for="prompt in defaultPrompts"
                :key="prompt.id"
                class="flex items-center gap-4"
              >
                <div
                  v-if="promptingFormatInput === 'classic'"
                  class="flex items-center justify-center flex-shrink-0 w-2 text-sm cursor-move handle text-grey-400"
                >
                  <BlIcon icon="drag" class="w-4 h-4" />
                </div>
                <div class="flex flex-grow truncate">
                  <PromptingClientItem
                    :prompt="prompt"
                    format="classic"
                    @update="onUpdatePrompt"
                    @delete="onRemovePrompt"
                    :disabledDelete="isUsedToSuccessMetric(prompt.name)"
                    :tooltipDelete="
                      isUsedToSuccessMetric(prompt.name)
                        ? 'You cannot delete this prompt because it has been used as a success metric.'
                        : ''
                    "
                  />
                </div>
              </div>
            </draggable>

            <div
              v-if="promptingFormatInput === 'custom'"
              class="flex flex-col gap-3"
            >
              <div
                v-for="group in groupedPrompts"
                :key="group.score"
                :id="group.score"
                class="flex gap-2"
              >
                <div
                  class="flex items-center justify-center flex-shrink-0 w-20 text-center border rounded bg-prim-100 border-prim-100"
                >
                  <BlTextBody1
                    :text="group.score"
                    class="font-semibold text-light-purple"
                  />
                </div>

                <div class="flex flex-col flex-grow gap-3 truncate">
                  <PromptingClientItem
                    v-for="prompt in group.prompts"
                    :key="prompt.id"
                    :prompt="prompt"
                    format="custom"
                    @update="onUpdatePrompt"
                    @delete="onRemovePrompt"
                    :disabledDelete="isUsedToSuccessMetric(prompt.name)"
                    :tooltipDelete="
                      isUsedToSuccessMetric(prompt.name)
                        ? 'You cannot delete this prompt because it has been used as a success metric.'
                        : ''
                    "
                  />
                </div>
              </div>
            </div>

            <div class="flex">
              <BlInputButton
                type="plain"
                icon="plus"
                label="Add prompt"
                class="px-0"
                @clicked="addPromptingOpened = true"
              />
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-4 px-4 py-4 border rounded bg-slate-100 border-slate-400"
        >
          <div class="flex flex-col gap-2">
            <BlTextSubtitle2 text="Goal and success metric" />
            <BlTextBody2
              v-if="promptingFormatInput === 'classic'"
              text="Mark this target as successful when the client uses a specific prompt at least a certain number of times."
            />
            <BlTextBody2
              v-if="promptingFormatInput === 'custom'"
              text="The target passes if the client's score meets the goal you set."
            />
          </div>
          <div
            v-if="promptingFormatInput === 'classic'"
            class="grid grid-cols-2 gap-4"
          >
            <BlSelectBox
              :key="`success-metric-${JSON.stringify(defaultPrompts)}`"
              label="Prompt level"
              asterisk
              placeholder="Select prompt"
              v-model="successMetricInput"
              :options="
                defaultPrompts.map(i => ({ value: i.name, label: i.name }))
              "
              @change="successMetricInput = $event"
            />
            <BlInputText :input="goalPromptInput" @changed="inputHandler" />
          </div>
          <div
            v-if="promptingFormatInput === 'custom'"
            class="grid grid-cols-2 gap-4"
          >
            <BlInputText :input="goalInput" @changed="inputHandler" />
            <BlSelectBox
              label="Success metric"
              asterisk
              v-model="successMetricInput"
              placeholder="Select success metric"
              :options="successMetricOptions"
              @change="successMetricInput = $event"
            />
          </div>
        </div>
      </div>

      <div
        v-if="typeInput.includes('Sbt')"
        class="flex flex-col gap-4 px-10 py-10 border w-152 bg-pure-white rounded-3xl border-slate-400"
      >
        <div class="flex flex-col gap-2">
          <BlTextSubtitle1 text="Prompt settings" />
        </div>

        <div
          v-if="promptLoading"
          class="flex items-center justify-center w-full h-100"
        >
          <div class="blum-loader"></div>
        </div>
        <div v-else class="flex flex-col gap-4">
          <div
            class="flex items-center justify-center border rounded h-72 bg-slate-100 border-slate-400"
          >
            <SbtPromptPreview
              :prompts="defaultPrompts"
              :style="{ transform: 'scale(0.5 , 0.5)' }"
            />
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <div class="flex-shrink-0 w-2" />
              <BlTextSubtitle4 text="Score (%)" class="flex-shrink-0 w-20" />
              <BlTextSubtitle4 text="Code" class="flex-shrink-0 w-10" />
              <BlTextSubtitle4 text="Prompt" class="w-full" />
            </div>

            <div class="flex flex-col gap-3">
              <div
                v-for="group in groupedPrompts"
                :key="group.score"
                :id="group.score"
                class="flex gap-2"
              >
                <div
                  class="flex items-center justify-center flex-shrink-0 w-20 text-center border rounded bg-prim-100 border-prim-100"
                >
                  <BlTextBody1
                    :text="group.score"
                    class="font-semibold text-light-purple"
                  />
                </div>

                <div class="flex flex-col flex-grow gap-3 truncate">
                  <SbtPromptClientItem
                    v-for="prompt in group.prompts"
                    :key="prompt.id"
                    :prompt="prompt"
                    @update="onUpdatePrompt"
                    @delete="onRemovePrompt"
                    :disabledDelete="isUsedToSuccessMetric(prompt.name)"
                    :tooltipDelete="
                      isUsedToSuccessMetric(prompt.name)
                        ? 'You cannot delete this prompt because it has been used as a success metric.'
                        : ''
                    "
                  />
                </div>
              </div>
            </div>

            <div class="flex">
              <BlInputButton
                type="plain"
                icon="plus"
                label="Add prompt"
                class="px-0"
                @clicked="addPromptSbtOpened = true"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="typeInput && !typeInput.includes('Sbt')"
        class="flex flex-col gap-4 px-10 py-10 border w-152 bg-pure-white rounded-3xl border-slate-400"
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <div class="p-1 bg-orange-300" :style="{ borderRadius: '4px' }">
              <BlIcon icon="tooltip" class="w-5 h-5" />
            </div>
            <BlTextSubtitle1 text="Action recommendations" />
          </div>
          <BlTextBody2
            text="Get recommendation after the session when client passes certain conditions."
            class="text-slate-700"
          />
        </div>
        <ActionRecommendationInput
          :totalSuccess="totalSuccessInput"
          :consecutiveSuccess="consecutiveSuccessInput"
          @input="onInputActionRecommendation($event)"
        />
      </div>
    </div>
    <NewTaskOverlay
      v-else-if="sbtStep == 1"
      :defaultTasks="defaultTasks"
      :tasksCodes="tasksCodes"
      @addTask="onAddTask"
      @updateTask="onUpdateTask"
      @removeTask="onRemoveTask"
    />
    <NewProblemOverlay
      v-else-if="sbtStep == 2"
      :defaultProblemBehaviors="defaultProblemBehaviors"
      :problemBehaviors="problemBehaviors"
      @addProblemBehavior="onAddProblemBehavior"
      @updateProblemBehavior="onUpdateProblemBehavior"
      @removeProblemBehavior="onRemoveProblemBehavior"
    />

    <div
      class="sticky bottom-0 z-10 flex items-center justify-between flex-shrink-0 h-20 px-6 border-t bg-pure-white border-slate-400"
    >
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 overflow-hidden">
          <div class="w-20 h-1 rounded bg-light-purple"></div>
          <div v-if="typeInput.includes('Sbt')" class="flex gap-2">
            <div
              class="w-20 h-1 rounded "
              :class="sbtStep >= 1 ? 'bg-light-purple' : 'bg-slate-400'"
            ></div>
            <div
              class="w-20 h-1 rounded"
              :class="sbtStep >= 2 ? 'bg-light-purple' : 'bg-slate-400'"
            ></div>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <div
            class="grid flex-shrink-0 w-6 h-6 border rounded-full place-items-center"
            :class="[
              isDisabledSubmit ? 'border-slate-600' : 'border-success-400'
            ]"
          >
            <BlTextSubtitle4
              v-if="isDisabledSubmit && !typeInput.includes('Sbt')"
              text="1"
            />
            <BlTextSubtitle4
              v-else-if="typeInput.includes('Sbt') && sbtStep === 0"
              text="1"
            />
            <BlTextSubtitle4
              v-else-if="typeInput.includes('Sbt') && sbtStep === 1"
              text="2"
            />
            <BlTextSubtitle4
              v-else-if="
                typeInput.includes('Sbt') && sbtStep === 2 && isDisabledSubmit
              "
              text="3"
            />
            <BlIcon
              v-else-if="
                typeInput.includes('Sbt') && sbtStep === 2 && !isDisabledSubmit
              "
              icon="check"
              color="success-4"
              class="w-6 h-6"
            />
            <BlIcon v-else icon="check" color="success-4" class="w-6 h-6" />
          </div>
          <BlTextSubtitle4 text="Target details" v-if="sbtStep == 0" />
          <BlTextSubtitle4
            text="Add tasks"
            v-if="sbtStep == 1 && typeInput.includes('Sbt')"
          />
          <BlTextSubtitle4
            text="Add problem behaviors"
            v-if="sbtStep == 2 && typeInput.includes('Sbt')"
          />
        </div>
      </div>
      <div class="flex gap-4" v-if="typeInput.includes('Sbt')">
        <BlInputButton
          v-if="sbtStep > 0"
          type="outlined"
          label="Back"
          :loading="submitLoading"
          @clicked="onBack"
        />
        <BlInputButton
          v-if="sbtStep === 0"
          type="outlined"
          label="Next"
          :disabled="isDisabledSubmit"
          :loading="submitLoading"
          @clicked="onNext"
        />
        <BlInputButton
          v-if="sbtStep < 2 && sbtStep > 0"
          type="outlined"
          label="Next"
          :disabled="isDisabledNext"
          :loading="submitLoading"
          @clicked="onNext"
        />
        <BlInputButton
          v-if="sbtStep === 2"
          label="Create target"
          :disabled="isDisabledSubmit"
          :loading="submitLoading"
          @clicked="onSubmit"
        />
      </div>
      <BlInputButton
        v-else
        label="Create target"
        :disabled="isDisabledSubmit"
        :loading="submitLoading"
        @clicked="onSubmit"
      />
    </div>

    <!-- prompting method -->
    <BlBackdrop
      v-if="addPromptingOpened"
      title="Add new prompt"
      label="Add prompt"
      @backdrop-close="closeAddPromptModal()"
      @backdrop-action="onAddPrompt('Prompting')"
    >
      <div class="px-8" :style="{ width: '40rem' }">
        <div class="grid items-center grid-cols-5 gap-4 py-4">
          <BlInputText
            v-if="promptingFormatInput === 'custom'"
            class="col-span-2"
            :input="scoreInput"
            @changed="inputHandler"
          />
          <BlInputText
            :class="{
              'col-span-4': promptingFormatInput === 'classic',
              'col-span-2': promptingFormatInput === 'custom'
            }"
            :input="codeInput"
            @changed="inputHandler"
          />
          <div class="relative col-span-1">
            <label :data-id="`prompt-symbol`" class="relative mb-1">
              <BlTextBody3 :data-id="`prompt-symbol`" text="Symbol" />
            </label>
            <div
              :data-id="`prompt-symbol`"
              :class="[
                symbolOpened ? 'border-light-purple' : 'border-grey-200'
              ]"
              class="relative flex items-center justify-between w-full h-10 px-4 border rounded cursor-pointer"
              @click.prevent="symbolOpened = !symbolOpened"
            >
              <BlIcon
                v-if="promptShapeInput && promptColorInput"
                class="w-6 h-6"
                :icon="'prompt-' + promptShapeInput"
                :color="promptColorInput"
              />
              <BlIcon
                :data-id="`prompt-symbol`"
                class="absolute w-5 h-5 transition-all duration-300 right-4"
                icon="chevron-down"
                :style="symbolOpened ? 'rotate: 180deg' : ''"
              />
            </div>
            <div
              :data-id="`prompt-symbol`"
              v-if="symbolOpened"
              class="absolute right-0 z-10 px-4 py-4 mt-2 space-y-4 overflow-y-auto border rounded shadow-md w-88 max-h-48 bg-pure-white border-grey-200"
            >
              <div :data-id="`prompt-symbol`">
                <BlTextBody2 text="Colors:" />
                <div :data-id="`prompt-symbol`" class="flex gap-2">
                  <div
                    :data-id="`prompt-symbol`"
                    v-for="color in Object.keys(promptColors)"
                    :key="color"
                    class="flex-shrink-0 w-6 h-6 border-2 rounded cursor-pointer"
                    :class="[
                      promptColorInput === color
                        ? 'border-light-purple'
                        : 'border-transparent'
                    ]"
                    :style="{
                      backgroundColor: promptColors[color].primaryColor
                    }"
                    @click="promptColorInput = color"
                    :title="color"
                  />
                </div>
              </div>
              <div :data-id="`prompt-symbol`">
                <BlTextBody2 text="Shapes:" />
                <div :data-id="`prompt-symbol`" class="flex gap-2">
                  <div
                    :data-id="`prompt-symbol`"
                    v-for="shape in Object.keys(shapes)"
                    :key="shape"
                    class="flex-shrink-0 w-8 h-8 text-center cursor-pointer"
                    @click="promptShapeInput = shape"
                    :title="shape"
                  >
                    <BlIcon
                      :data-id="`prompt-symbol`"
                      :icon="shapes[shape].icon"
                      :color="
                        promptShapeInput === shape
                          ? 'prompt-active'
                          : 'prompt-idle'
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <BlInputText
            class="col-span-5"
            :input="promptNameInput"
            @changed="inputHandler"
          />
        </div>
      </div>
    </BlBackdrop>

    <!-- sbt method -->
    <BlBackdrop
      v-if="addPromptSbtOpened"
      title="Add new prompt"
      label="Add prompt"
      @backdrop-close="closeAddPromptModal()"
      @backdrop-action="onAddPrompt('Sbt')"
    >
      <div class="px-8" :style="{ width: '40rem' }">
        <div class="grid items-center grid-cols-6 gap-4 py-4">
          <BlInputText
            class="col-span-3"
            :input="scoreInput"
            @changed="inputHandler"
          />
          <BlInputText
            class="col-span-3"
            :input="codeInput"
            @changed="inputHandler"
          />
          <BlInputText
            class="col-span-6"
            :input="promptNameInput"
            @changed="inputHandler"
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import draggable from "vuedraggable";

import PromptingPreview from "@/components/repository/prompting/Preview.vue";
import PromptingClientItem from "@/components/repository/prompting/ClientItem.vue";
import SbtPromptPreview from "@/components/repository/sbt/PromptingPreview.vue";
import SbtPromptClientItem from "@/components/repository/sbt/PromptingClientItem.vue";
import ActionRecommendationInput from "../modal/ActionRecommendationInput.vue";
import NewTaskOverlay from "./NewTaskOverlay.vue";
import NewProblemOverlay from "./NewProblemOverlay.vue";

import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../../BlTextSubtitle4.vue";
import BlTextBody1 from "../../../BlTextBody1.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextBody3 from "../../../BlTextBody3.vue";
import BlBreadcrumbs from "../../../BlBreadcrumbs.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputTime from "../../../BlInputTime.vue";
import BlBackdrop from "../../../BlBackdrop.vue";
import BlSelectBox from "../../../BlSelectBox.vue";
import BlIcon from "../../../BlIcon.vue";
import BlInputToggle from "../../../BlInputToggle.vue";

export default {
  name: "NewTargetOverlay",
  components: {
    draggable,
    PromptingPreview,
    PromptingClientItem,
    SbtPromptPreview,
    SbtPromptClientItem,
    ActionRecommendationInput,
    NewTaskOverlay,
    NewProblemOverlay,
    //
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlInputButton,
    BlInputText,
    BlInputTime,
    BlBackdrop,
    BlSelectBox,
    BlIcon,
    BlInputToggle
  },
  props: {
    title: { type: String, default: "Create new target" },
    description: { type: String, default: "" },
    clientId: { type: Number | String, default: null },
    kind: { type: String, default: "client" },
    defaultCurriculum: { type: Object, default: () => null },
    duplicateTargetId: { type: Number, default: null }
    // $emit('close-modal')
    // $emit('after-commit')
  },
  data() {
    const chipClass =
      "h-6 px-2 flex items-center rounded-full flex-shrink-0 text-xs";
    return {
      modalLoading: true,
      submitLoading: false,
      promptLoading: false,
      addPromptingOpened: false,
      addPromptSbtOpened: false,
      //
      nameInput: {
        label: "Title",
        placeholder: "Type title",
        model: "",
        asterisk: true
      },
      curriculumIdInput: "",
      typeInput: "",
      typeOptions: [
        { value: "Target::Duration", label: "Duration" },
        { value: "Target::Frequency", label: "Frequency" },
        { value: "Target::Percentage", label: "Percentage" },
        { value: "Target::Pir", label: "Partial Interval Recording" },
        { value: "Target::Prompting", label: "Prompting" },
        { value: "Target::Sbt", label: "Skill-Based Treatment (SBT)" }
      ],
      statusInput: "pending",
      statusOptions: [
        {
          value: "pending",
          label: "Pending",
          class: `${chipClass} text-slate-800 bg-slate-300`
        },
        {
          value: "in_progress",
          label: "In acquisition",
          class: `${chipClass} text-tulip-800 bg-tulip-200`
        }
      ],
      descriptionInput: {
        label: "Description",
        placeholder: "Type description",
        model: "",
        textarea: true
      },
      goalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        suffix: "percent (%)",
        asterisk: true
      },
      goalFrequencyInput: {
        label: "Goal",
        placeholder: "Type goal",
        model: "5",
        suffix: "attempt(s) per session",
        asterisk: true
      },
      goalPromptInput: {
        label: "Minimum attempts per session",
        placeholder: "Type goal",
        model: "2",
        suffix: "attempt(s)",
        asterisk: true
      },
      trialsInput: {
        label: "Number of trials",
        placeholder: "Type number",
        model: "",
        asterisk: true
      },
      timeInput: {
        label: "Goal time (hours/minutes/seconds)",
        model: "",
        asterisk: true
      },
      intervalInput: {
        label: "Interval",
        placeholder: "Target interval",
        model: "5",
        suffix: "Minutes",
        disabled: true
      },
      durationInput: {
        label: "Duration",
        placeholder: "Minimum 5 minutes",
        model: "5",
        asterisk: true,
        suffix: "Minutes"
      },
      promptingFormatInput: "classic", // classic | custom
      promptingFormatOptions: [
        { value: "classic", label: "Classic" },
        { value: "custom", label: "Custom" }
      ],
      successMetricInput: "",
      successMetricOptions: [
        {
          value: "equal to or greater than goal",
          label: "Equal to or greater than goal"
        },
        { value: "less than goal", label: "Less than goal" }
      ],
      //
      defaultPrompts: [],
      defaultTasks: [],
      defaultProblemBehaviors: [],
      promptPageIndex: 0,
      //
      probingEnableInput: false,
      probingTrialInput: {
        label: "Minimum probes (trials) needed",
        placeholder: "Type number",
        model: "",
        asterisk: true,
        suffix: "trials"
      },
      probingGoalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        asterisk: true,
        suffix: "percent (%)"
      },
      //
      totalSuccessChecked: true,
      totalSuccessInput: 2,
      consecutiveSuccessChecked: true,
      consecutiveSuccessInput: 2,
      // Prompting or SBT new prompt modal
      scoreInput: {
        label: "Score",
        placeholder: "Type score",
        model: "",
        inputType: "number",
        suffix: "%",
        min: 0,
        max: 100
      },
      codeInput: {
        label: "Code",
        placeholder: "Type code",
        model: ""
      },
      promptColorInput: "cherry",
      promptShapeInput: "square",
      promptNameInput: {
        label: "Prompt name",
        placeholder: "Type name",
        model: ""
      },
      symbolOpened: false,
      //
      sbtStep: 0,
      highestPosition: 0,
      vacantPositionsTask: [],
      vacantPositionsProblemBehavior: [],
      //
      promptColors: {
        cherry: {
          primaryColor: "#FF4447",
          secondaryColor: "#FF4447",
          textColor: "#FFFFFF"
        },
        blush: {
          primaryColor: "#FFC1C2",
          secondaryColor: "#FF4447",
          textColor: "#FF4447"
        },
        gold: {
          primaryColor: "#F4D430",
          secondaryColor: "#F4D430",
          textColor: "#FFFFFF"
        },
        daffodil: {
          primaryColor: "#FBEEAC",
          secondaryColor: "#F4D430",
          textColor: "#9B7600"
        },
        lake: {
          primaryColor: "#2AB1A8",
          secondaryColor: "#2AB1A8",
          textColor: "#FFFFFF"
        },
        mint: {
          primaryColor: "#B8E5E2",
          secondaryColor: "#2AB1A8",
          textColor: "#2AB1A8"
        },
        sky: {
          primaryColor: "#487ADF",
          secondaryColor: "#487ADF",
          textColor: "#FFFFFF"
        },
        hydrangeas: {
          primaryColor: "#D3DFF7",
          secondaryColor: "#487ADF",
          textColor: "#487ADF"
        },
        grey: {
          primaryColor: "#E4E4E4",
          secondaryColor: "#A0A0A0",
          textColor: "#A0A0A0"
        },
        primary: {
          primaryColor: "#9A8AC2",
          secondaryColor: "#9A8AC2",
          textColor: "#FFFFFF"
        }
      },
      shapes: {
        square: { icon: "prompt-square", pointStyle: "rectRounded" },
        circle: { icon: "prompt-circle", pointStyle: "circle" },
        triangle: { icon: "prompt-triangle", pointStyle: "triangle" },
        diamond: { icon: "prompt-diamond", pointStyle: "rectRot" }
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "centerPrompts",
      "tasksCodes",
      "problemBehaviors",
      "curriculums",
      "currentCenter"
    ]),
    breadcrumbs() {
      let currentLabel = this.title;
      const action = () => this.$emit("close-modal");
      return [
        { label: "Targets", action },
        { label: currentLabel, action, truncate: true }
      ];
    },
    defaultCurriculumOptions() {
      return this.curriculums.map(curriculum => ({
        value: curriculum.id,
        label: curriculum.name,
        prefixColor: curriculum.color
      }));
    },
    //
    groupedPrompts() {
      return this.groupPromptsByScore(this.defaultPrompts);
    },
    //
    isDisabledNext() {
      if (this.sbtStep === 1) {
        const isEmpty = this.defaultTasks.some(
          task => !task.code || !task.title
        );
        return isEmpty;
      }
      return false;
    },
    //
    isDisabledSubmit() {
      // Basic checks
      if (!this.nameInput.model) return true;
      if (!this.typeInput) return true;
      if (!this.curriculumIdInput) return true;
      // Type-specific checks
      if (this.typeInput.includes("Percentage")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.trialsInput.model) return true;
        // Probing for percentage
        if (this.probingEnableInput) {
          if (!this.probingTrialInput.model) return true;
          if (!this.probingGoalInput.model) return true;
        }
      }
      if (this.typeInput.includes("Duration")) {
        if (!this.successMetricInput) return true;
        if (this.timeInput.model === "00:00:00") return true;
        if (!this.timeInput.model) return true;
      }
      if (this.typeInput.includes("Pir")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.intervalInput.model) return true;
        if (!this.durationInput.model) return true;
      }
      if (this.typeInput.includes("Frequency")) {
        if (!this.successMetricInput) return true;
        if (!this.goalFrequencyInput.model) return true;
      }
      if (this.typeInput.includes("Prompting")) {
        if (!this.successMetricInput) return true;
        if (this.promptingFormatInput === "classic") {
          if (!this.goalPromptInput.model) return true;
        }
        if (this.promptingFormatInput === "custom") {
          if (!this.goalInput.model) return true;
        }
      }
      // Action recommendation checks
      if (this.totalSuccessChecked) {
        if (this.totalSuccessInput === "") return true;
      }
      if (this.consecutiveSuccessChecked) {
        if (this.consecutiveSuccessInput === "") return true;
      }
      // SBT check step
      if (this.sbtStep === 2) {
        const isEmpty = this.defaultProblemBehaviors.some(
          pb => !pb.code || !pb.code_definition
        );
        return isEmpty;
      }
      return false;
    },
    filterTypeOptions() {
      return this.typeOptions.filter(
        type => type.value !== "Target::Sbt" || this.currentCenter.enable_sbt
      );
    }
  },
  watch: {
    async typeInput(newType) {
      if (this.duplicateTargetId) return;
      this.successMetricInput = "";
      if (newType && newType.includes("Prompting")) {
        const type = "Prompt::Prompting";
        const params = `?type=${type}&prompting_format=${this.promptingFormatInput}`;
        await this.fetchCenterPrompts(params);
      }
      if (newType && newType.includes("Sbt")) {
        const type = "Prompt::Sbt";
        const params = `?type=${type}`;
        await this.fetchCenterPrompts(params);
        await this.fetchTasksCodes();
        await this.fetchProblemBehaviors();
      }
    },
    async promptingFormatInput(newFormat) {
      if (this.duplicateTargetId) return;
      this.successMetricInput = "";
      const type = this.typeInput.includes("Prompting")
        ? "Prompt::Prompting"
        : "Prompt::Sbt";
      const params = `?type=${type}&prompting_format=${newFormat}`;
      await this.fetchCenterPrompts(params);
    },
    symbolOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  async mounted() {
    this.modalLoading = true;
    this.promptPageIndex = 0;
    this.onResetInputs();

    await this.getCurriculums({ params: "?sort=name_asc" });

    if (this.defaultCurriculum) {
      this.curriculumIdInput = this.defaultCurriculum.id;
    }

    this.defaultTasks.forEach((task, index) => {
      if (!task.position) {
        task.position = index + 1;
        this.highestPosition = Math.max(this.highestPosition, task.position);
      }
    });

    if (this.duplicateTargetId) {
      const params = {
        url: `/api/v1/targets/${this.duplicateTargetId}`
      };
      const { success, data } = await this.actionGet(params);
      if (success) {
        this.onAssignInputs(data);
      }
    }

    this.modalLoading = false;
  },
  methods: {
    ...mapActions([
      "actionGet",
      "createTarget",
      "getCenterPrompts",
      "createActionRecommendation",
      "showToast",
      "getTasksCodes",
      "getProblemBehaviors",
      "getCurriculums"
    ]),
    //
    async fetchTasksCodes() {
      const { success, data } = await this.getTasksCodes();
      if (!success) return;
      this.generateDefaultTasks(data);
    },
    async fetchProblemBehaviors() {
      const { success, data } = await this.getProblemBehaviors();
      if (!success) return;
      this.generateDefaultProblemBehaviors(data);
    },
    async fetchCenterPrompts(promptParams) {
      const params = { params: promptParams };
      this.promptLoading = true;
      const { success, data } = await this.getCenterPrompts(params);
      this.promptLoading = false;
      if (!success) return;
      this.generateDefaultPrompts(data);
    },
    //
    groupPromptsByScore(prompts) {
      const sortedPrompts = prompts.sort((a, b) => {
        if (b.score === a.score) {
          return a.is_default ? 1 : -1;
        }
        return b.score - a.score;
      });

      const grouped = sortedPrompts.reduce((acc, prompt) => {
        const score = prompt.score;
        if (!acc[score]) {
          acc[score] = [];
        }
        acc[score].push(prompt);
        return acc;
      }, {});

      const groupedArray = Object.keys(grouped).map(score => {
        return {
          score: parseInt(score),
          prompts: grouped[score].sort((a, b) => a.position - b.position)
        };
      });

      return groupedArray.sort((a, b) => b.score - a.score);
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    getRandomId(prefix) {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `${prefix}-${combination}`;
    },
    onResetInputs() {
      // base
      this.nameInput.model = "";
      this.typeInput = "";
      this.curriculumIdInput = "";
      this.descriptionInput.model = "";
      // action recommendation
      this.totalSuccessChecked = true;
      this.totalSuccessInput = 2;
      this.consecutiveSuccessChecked = true;
      this.consecutiveSuccessInput = 2;
      // percentage
      this.successMetricInput = "";
      this.goalInput.model = "";
      this.trialsInput.model = "";
      this.probingEnableInput = false;
      this.probingTrialInput.model = "";
      this.probingGoalInput.model = "";
      // duration
      this.successMetricInput = "";
      this.timeInput.model = "";
      // pir
      this.successMetricInput = "";
      this.goalInput.model = "";
      this.intervalInput.model = "5";
      this.durationInput.model = "";
      // frequency
      this.successMetricInput = "";
      this.goalFrequencyInput.model = "5";
      // prompting
      this.successMetricInput = "";
      this.goalPromptInput.model = "2"; // classic
      this.goalInput.model = ""; // custom
      this.defaultPrompts = [];
      // sbt
      this.defaultPrompts = [];
      this.defaultTasks = [];
      this.defaultProblemBehaviors = [];
    },
    onAssignInputs(target) {
      // base
      this.nameInput.model = `Copy of ${target.name}`;
      this.typeInput = target.type;
      this.curriculumIdInput = target.curriculum_id;
      this.descriptionInput.model = target.description;
      // action recommendation
      if (
        target.action_recommendations &&
        target.action_recommendations.length
      ) {
        const action = target.action_recommendations[0];
        this.totalSuccessInput = action.total_success;
        if (!action.total_success) {
          this.totalSuccessChecked = false;
        }
        this.consecutiveSuccessInput = action.consecutive_success;
        if (!action.consecutive_success) {
          this.consecutiveSuccessChecked = false;
        }
      }
      // percentage
      if (target.type === "Target::Percentage") {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.trialsInput.model = target.number_of_trial;
        this.probingEnableInput = target.probing_enable;
        this.probingTrialInput.model = target.probing_number_of_trial;
        this.probingGoalInput.model = target.probing_goal;
      }
      // duration
      if (target.type === "Target::Duration") {
        this.successMetricInput = target.success_metric;
        this.timeInput.model = target.goal_time;
      }
      // pir
      if (target.type === "Target::Pir") {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.intervalInput.model = target.interval;
        this.durationInput.model = target.duration;
      }
      // frequency
      if (target.type === "Target::Frequency") {
        this.successMetricInput = target.success_metric;
        this.goalFrequencyInput.model = target.goal;
      }
      // prompting
      if (target.type === "Target::Prompting") {
        this.successMetricInput = target.success_metric;
        this.promptingFormatInput = target.prompting_format;
        if (target.prompting_format === "classic") {
          this.goalPromptInput.model = target.goal;
        }
        if (target.prompting_format === "custom") {
          this.goalInput.model = target.goal;
        }
        this.defaultPrompts = target.prompts;
      }
      // sbt
      if (target.type === "Target::Sbt") {
        this.defaultPrompts = target.prompts;
        this.defaultTasks = target.target_tasks.map(i => {
          return {
            ...i,
            id: i.task_code_id,
            key: this.getRandomId("target-task-code")
          };
        });
        this.defaultProblemBehaviors = target.target_problem_behaviors.map(
          i => {
            return {
              ...i,
              id: i.problem_behavior_id,
              key: this.getRandomId("target-problem-behavior")
            };
          }
        );
      }
    },
    closeAddPromptModal() {
      this.addPromptingOpened = false;
      this.addPromptSbtOpened = false;
      this.scoreInput.model = "";
      this.codeInput.model = "";
      this.promptColorInput = "cherry";
      this.promptShapeInput = "square";
      this.promptNameInput.model = "";
    },
    //
    generateDefaultPrompts(val = []) {
      if (val && val.length) {
        const arr = [...val]
          .sort((a, b) => a.position - b.position)
          .sort((a, b) => {
            // sort by highest score
            if (this.promptingFormatInput === "custom") {
              if (a.is_default && a.abbreviation === "Id") return -1;
              if (b.is_default && b.abbreviation === "Id") return 1;

              if (a.is_default && a.abbreviation === "Ic") return 1;
              if (b.is_default && b.abbreviation === "Ic") return -1;
              return b.score - a.score;
            }
            return 1;
          });
        this.defaultPrompts = arr;
      }
    },
    generateDefaultTasks(tasks = []) {
      if (tasks && tasks.length > 0) {
        this.defaultTasks = [...tasks].map(i => {
          return { key: this.getRandomId("target-task-code"), ...i };
        });
      }
    },
    generateDefaultProblemBehaviors(problemBehaviors = []) {
      if (problemBehaviors && problemBehaviors.length > 0) {
        this.defaultProblemBehaviors = [...problemBehaviors].map(i => {
          return { key: this.getRandomId("target-problem-behavior"), ...i };
        });
      }
    },
    isUsedToSuccessMetric(promptName) {
      return promptName === this.successMetricInput;
    },
    //
    onAddPrompt(type) {
      let length = 0;
      let prompt = {};
      if (this.defaultPrompts && this.defaultPrompts.length) {
        length = this.defaultPrompts.length;
      }
      if (type === "Prompting") {
        prompt = {
          id: this.getRandomId("new-prompt"),
          name: this.promptNameInput.model || "New Prompt",
          abbreviation: this.codeInput.model || "XX",
          color: this.promptColorInput || "cherry",
          shape: this.promptShapeInput || "square",
          position: length + 1,
          prompting_format: this.promptingFormatInput,
          score: null
        };
        if (this.promptingFormatInput === "custom") {
          prompt.score = this.scoreInput.model || 0;
        }
      }
      if (type === "Sbt") {
        prompt = {
          id: this.getRandomId("new-prompt"),
          score: this.scoreInput.model || 0,
          abbreviation: this.codeInput.model || "XX",
          name: this.promptNameInput.model || "New Prompt",
          position: length + 1
        };
      }

      this.addPromptingOpened = false;
      this.addPromptSbtOpened = false;
      this.scoreInput.model = "";
      this.codeInput.model = "";
      this.promptColorInput = "cherry";
      this.promptShapeInput = "square";
      this.promptNameInput.model = "";
      const arr = [...this.defaultPrompts, prompt];
      this.generateDefaultPrompts(arr);
    },
    onAddTask() {
      let maxPosition = 0;
      if (this.defaultTasks && this.defaultTasks.length) {
        maxPosition = Math.max(...this.defaultTasks.map(i => i.position));
      }
      const newTask = {
        key: this.getRandomId("target-task-code"),
        id: 0,
        code: "",
        title: "",
        code_definition: "",
        description: "",
        color: "#2AB1A8",
        position: maxPosition + 1
      };
      const arr = [...this.defaultTasks, newTask];
      this.defaultTasks = arr;
    },
    onAddProblemBehavior() {
      let maxPosition = 0;
      if (this.defaultProblemBehaviors && this.defaultProblemBehaviors.length) {
        maxPosition = Math.max(
          ...this.defaultProblemBehaviors.map(i => i.position)
        );
      }
      const newProblemBehavior = {
        key: this.getRandomId("target-problem-behavior"),
        id: 0,
        code: "",
        code_definition: "",
        description: "",
        color: "#DD3F30",
        position: maxPosition + 1
      };
      const arr = [...this.defaultProblemBehaviors, newProblemBehavior];
      this.defaultProblemBehaviors = arr;
    },
    //
    onUpdatePrompt(data) {
      const index = this.defaultPrompts.findIndex(i => i.id === data.id);
      if (index > -1) {
        this.defaultPrompts.splice(index, 1, data);
      }
      this.generateDefaultPrompts(this.defaultPrompts);
    },
    onUpdateTask(data) {
      const index = this.defaultTasks.findIndex(i => i.key === data.key);
      if (index > -1) {
        this.defaultTasks.splice(index, 1, data);
      }
    },
    onUpdateProblemBehavior(data) {
      const index = this.defaultProblemBehaviors.findIndex(
        i => i.key === data.key
      );
      if (index > -1) {
        this.defaultProblemBehaviors.splice(index, 1, data);
      }
    },
    //
    onRemovePrompt(data) {
      const arr = [...this.defaultPrompts]
        .filter(i => i.id !== data.id)
        .map((i, n) => ({ ...i, position: n + 1 }));
      this.generateDefaultPrompts(arr);
    },
    onRemoveTask(data) {
      const taskToRemove = this.defaultTasks.find(i => i.key === data.key);
      if (taskToRemove && taskToRemove.position) {
        this.vacantPositionsTask.push(taskToRemove.position);
        this.vacantPositionsTask.sort((a, b) => a - b);
      }

      // Remove the task
      const arr = [...this.defaultTasks].filter(i => i.key !== data.key);
      this.defaultTasks = arr;
    },
    onRemoveProblemBehavior(data) {
      const problemBehaviorToRemove = this.defaultProblemBehaviors.find(
        i => i.key === data.key
      );
      if (problemBehaviorToRemove && problemBehaviorToRemove.position) {
        this.vacantPositionsProblemBehavior.push(
          problemBehaviorToRemove.position
        );
        this.vacantPositionsProblemBehavior.sort((a, b) => a - b);
      }
      const arr = [...this.defaultProblemBehaviors].filter(
        i => i.key !== data.key
      );
      this.defaultProblemBehaviors = arr;
    },
    //
    onMovePosition(event) {
      function arrayMove(arr, oldIndex, newIndex) {
        while (oldIndex < 0) {
          oldIndex += arr.length;
        }
        while (newIndex < 0) {
          newIndex += arr.length;
        }
        if (newIndex >= arr.length) {
          var k = newIndex - arr.length + 1;
          while (k--) {
            arr.push(undefined);
          }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
        return arr; // for testing
      }

      const oldIndex = event.oldIndex;
      const newIndex = event.newIndex;
      const base = [...this.defaultPrompts];
      const sorted = arrayMove(base, oldIndex, newIndex);

      const arr = [...sorted].map((i, n) => ({ ...i, position: n + 1 }));
      this.generateDefaultPrompts(arr);
    },
    //
    onInputActionRecommendation(val) {
      this.totalSuccessChecked = val.total_success_checked;
      this.totalSuccessInput = val.total_success;
      this.consecutiveSuccessChecked = val.consecutive_success_checked;
      this.consecutiveSuccessInput = val.consecutive_success;
    },
    //
    onNext() {
      this.sbtStep = this.sbtStep + 1;
    },
    onBack() {
      this.sbtStep = this.sbtStep - 1;
    },
    //
    async onSubmit() {
      const params = {
        kind: this.kind,
        target: {
          name: this.nameInput.model,
          type: this.typeInput,
          curriculum_id: this.curriculumIdInput,
          description: this.descriptionInput.model
        }
      };
      if (this.kind === "client") {
        params.client_id = this.clientId;
        params.target.status = this.statusInput;
      }

      if (this.typeInput === "Target::Percentage") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.number_of_trial = this.trialsInput.model;
        params.target.probing_enable = this.probingEnableInput;
        if (this.probingEnableInput) {
          params.target.probing_number_of_trial = this.probingTrialInput.model;
          params.target.probing_goal = this.probingGoalInput.model;
        }
        if (params.target.goal > 100) {
          const title = `Invalid input`;
          const message = `Goal must be less than or equal to 100 percent(%).`;
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.probing_enable && params.target.probing_goal > 100) {
          const title = `Invalid input`;
          const message = `Probing goal must be less than or equal to 100 percent(%).`;
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Duration") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal_time = this.timeInput.model;
      }

      if (this.typeInput === "Target::Pir") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.interval = this.intervalInput.model;
        params.target.duration = this.durationInput.model;
        if (params.target.goal > 100) {
          const title = `Invalid input`;
          const message = `Goal must be less than or equal to 100 percent(%).`;
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.duration < 5) {
          const title = `Invalid input`;
          const message = `Duration must be more than or equal to 5 minutes.`;
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Frequency") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalFrequencyInput.model;
      }

      if (this.typeInput === "Target::Prompting") {
        params.target.success_metric = this.successMetricInput;
        params.target.prompting_format = this.promptingFormatInput;
        if (this.promptingFormatInput === "classic") {
          params.target.goal = this.goalPromptInput.model;
        }
        if (this.promptingFormatInput === "custom") {
          params.target.goal = this.goalInput.model;
        }
        params.target.prompts_attributes = [...this.defaultPrompts].map(
          (i, n) => {
            const p = {
              name: i.name,
              abbreviation: i.abbreviation,
              shape: i.shape,
              color: i.color,
              position: n + 1
            };
            if (this.promptingFormatInput === "custom") {
              p.score = i.score;
              p.is_default = i.is_default;
            }
            return p;
          }
        );
      }

      if (this.typeInput === "Target::Sbt") {
        params.target.prompts_attributes = [...this.defaultPrompts].map(
          (i, n) => {
            return {
              name: i.name,
              score: i.score,
              abbreviation: i.abbreviation,
              is_default: i.is_default,
              position: n + 1
            };
          }
        );
        //
        params.target.target_tasks_attributes = [...this.defaultTasks].map(
          (i, n) => {
            return {
              task_code_id: i.id,
              title: i.title,
              description: i.description
            };
          }
        );
        //
        params.target.target_problem_behaviors_attributes = [
          ...this.defaultProblemBehaviors
        ].map((i, n) => {
          return {
            problem_behavior_id: i.id,
            title: i.code_definition,
            color: i.color,
            description: i.description
          };
        });
      }

      if (this.totalSuccessChecked && this.totalSuccessInput < 1) {
        const title = `Invalid input`;
        const message = `Please enter a value greater than or equal to 1 for total success.`;
        this.showToast({ title, message, type: "error" });
        return;
      }
      if (this.consecutiveSuccessChecked && this.consecutiveSuccessInput < 2) {
        const title = `Invalid input`;
        const message = `Please enter a value greater than or equal to 2 for consecutive.`;
        this.showToast({ title, message, type: "error" });
        return;
      }

      this.submitLoading = true;
      const { success, data: newTarget } = await this.createTarget(params);
      if (!success) {
        this.submitLoading = false;
        return;
      }

      if (this.typeInput !== "Target::Sbt") {
        const paramsRecommendation = {
          kind: this.kind,
          target_id: newTarget.id,
          action_recommendation: {
            target_id: newTarget.id,
            total_success: this.totalSuccessChecked
              ? this.totalSuccessInput
              : null,
            consecutive_success: this.consecutiveSuccessChecked
              ? this.consecutiveSuccessInput
              : null,
            recommended_action: "mastered"
          }
        };

        this.submitLoading = true;
        await this.createActionRecommendation(paramsRecommendation);
        this.submitLoading = false;
      }
      const title = "New target created";
      const message = "Success! New target has been created successfully.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (`prompt-symbol` === id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.symbolOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/overlay/NewTargetOverlay.vue